import { useState, useEffect, useCallback } from 'react';
import { storage } from '../utils/storage';

/**
 * Hook para manejar el prompt de instalación de PWA
 * Captura el evento beforeinstallprompt y permite mostrar el prompt de instalación
 */
export function useInstallPrompt() {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);

  useEffect(() => {
    // Verificar si ya está instalada
    const checkInstalled = () => {
      // Check display-mode media query
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
      // Check navigator.standalone for iOS
      const isIOSStandalone = window.navigator.standalone === true;
      // Check if launched from home screen on Android
      const isAndroidStandalone = document.referrer.includes('android-app://');

      setIsInstalled(isStandalone || isIOSStandalone || isAndroidStandalone);
    };

    checkInstalled();

    // Capturar el evento beforeinstallprompt
    const handleBeforeInstallPrompt = (e) => {
      // Prevenir el prompt automático del navegador
      e.preventDefault();
      // Guardar el evento para usarlo después
      setInstallPrompt(e);
      console.log('[PWA] Install prompt captured');
    };

    // Detectar cuando se instala la app
    const handleAppInstalled = () => {
      setInstallPrompt(null);
      setIsInstalled(true);
      setIsInstalling(false);
      console.log('[PWA] App installed successfully');

      // Guardar en localStorage
      storage.set('pwa-installed', 'true');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    // Check display mode changes
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    mediaQuery.addEventListener('change', checkInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      mediaQuery.removeEventListener('change', checkInstalled);
    };
  }, []);

  /**
   * Mostrar el prompt de instalación
   */
  const promptInstall = useCallback(async () => {
    if (!installPrompt) {
      console.log('[PWA] No install prompt available');
      return { outcome: 'unavailable' };
    }

    setIsInstalling(true);

    try {
      // Mostrar el prompt
      installPrompt.prompt();

      // Esperar la respuesta del usuario
      const { outcome } = await installPrompt.userChoice;

      console.log('[PWA] User choice:', outcome);

      if (outcome === 'accepted') {
        setInstallPrompt(null);
      }

      setIsInstalling(false);
      return { outcome };
    } catch (error) {
      console.error('[PWA] Install prompt error:', error);
      setIsInstalling(false);
      return { outcome: 'error', error };
    }
  }, [installPrompt]);

  /**
   * Descartar el prompt sin instalar
   */
  const dismissPrompt = useCallback(() => {
    storage.set('pwa-install-dismissed', Date.now().toString());
    setInstallPrompt(null);
  }, []);

  /**
   * Verificar si el prompt fue descartado recientemente (24 horas)
   */
  const wasRecentlyDismissed = useCallback(() => {
    const dismissed = storage.get('pwa-install-dismissed');
    if (!dismissed) return false;

    const dismissedTime = parseInt(dismissed, 10);
    const dayInMs = 24 * 60 * 60 * 1000;
    return Date.now() - dismissedTime < dayInMs;
  }, []);

  return {
    canInstall: !!installPrompt && !isInstalled,
    isInstalled,
    isInstalling,
    promptInstall,
    dismissPrompt,
    wasRecentlyDismissed
  };
}

export default useInstallPrompt;
